import { SidebarTreeViewModelItemContainer } from './SidebarTreeViewModelItemContainer';
import { useSidebarFolder } from './useSidebarFolder';
import { FolderItem } from '../items';
import { useEffect, useRef } from 'react';
import type { Workspace } from '@workspace/domain/workspace';
import type { Folder } from '@workspace/view-model-folder/domain/Folder';
import type { RootFolderTree } from '@workspace/view-model-folder/domain/FolderTree';

type Props = {
  workspace: Workspace;
  folder: Folder;
  rootFolderTree: RootFolderTree;
  shouldBeScrolled?: boolean;
};

export const SidebarTreeFolderItemContainer: React.FC<Props> = ({
  workspace,
  folder,
  rootFolderTree,
  shouldBeScrolled,
}: Props) => {
  const {
    isOpen,
    isSelected,
    firstLoaded,
    toggleFolder,
    sortedFolders,
    sortedViewModels,
    resetScrollFolder,
    scrolledViewModelId,
    scrolledFolderId,
    sidebarTreeContainerRef,
  } = useSidebarFolder(folder.id, rootFolderTree);

  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (shouldBeScrolled && ref.current && sidebarTreeContainerRef?.current) {
      const rect = ref.current.getBoundingClientRect();
      const treeContainer = sidebarTreeContainerRef.current;
      const { top: treeContainerTop, bottom: treeContainerBottom } = treeContainer.getBoundingClientRect();
      if (rect.top <= treeContainerTop || treeContainerBottom <= rect.bottom) {
        ref.current.scrollIntoView();
      }
      resetScrollFolder();
    }
  }, [shouldBeScrolled, ref, resetScrollFolder, sidebarTreeContainerRef]);

  return (
    <FolderItem
      ref={ref}
      folder={folder}
      workspace={workspace}
      folderTree={rootFolderTree}
      toggleFolder={toggleFolder}
      isOpen={isOpen}
      isSelected={isSelected}
      firstLoaded={firstLoaded}
    >
      <>
        {sortedFolders.map((folder) => (
          <SidebarTreeFolderItemContainer
            key={folder.id}
            workspace={workspace}
            folder={folder}
            rootFolderTree={rootFolderTree}
            shouldBeScrolled={scrolledFolderId === folder.id}
          />
        ))}
        {sortedViewModels.map((viewModel) => (
          <SidebarTreeViewModelItemContainer
            key={viewModel.id}
            workspace={workspace}
            viewModel={viewModel}
            shouldBeScrolled={scrolledViewModelId === viewModel.id}
          />
        ))}
      </>
    </FolderItem>
  );
};
